import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './HeroSlideshow.css';

const SLIDES = [
  {
    id: 1,
    eyebrow: 'Introducing the Stratos Standard',
    headline: 'Where Every\nChild Reaches\nNew Heights',
    sub: "Celebrate your child's amazing early achievements with an official SKIES certification that's recognized, prestigious, and uniquely theirs.",
    cta: { label: 'Book a Session', to: '/book' },
    cta2: { label: 'Learn More', to: '/services' },
    badge: 'SKIES Certified',
    bg: 'slide-1',
  },
  {
    id: 2,
    eyebrow: 'Aero: Achieving Early Results & Outcomes',
    headline: 'Early Achievers\nDeserve\nRecognition',
    sub: 'When your child masters something new, it deserves to be celebrated. Our SKIES consultants verify the achievement and record it in the Stratos Registry.',
    cta: { label: 'View Our Services', to: '/services' },
    cta2: { label: 'Book Today', to: '/book' },
    badge: 'Ages 0–6',
    bg: 'slide-2',
  },
  {
    id: 3,
    eyebrow: 'Your Child\'s First Trophy',
    headline: 'Official\nCertification\nThey\'ll Treasure',
    sub: 'A formal certification session. A beautiful certificate. A moment of pride your child will remember, and so will you.',
    cta: { label: 'Get Started', to: '/book' },
    cta2: { label: 'Read Our Blog', to: '/blog' },
    badge: 'Prestige & Joy',
    bg: 'slide-3',
  },
];

export default function HeroSlideshow() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 420);
  }, [animating, current]);

  const next = useCallback(() => goTo((current + 1) % SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [current, goTo]);

  // Auto-advance
  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next]);

  const slide = SLIDES[current];

  return (
    <section className={`hero-slideshow ${slide.bg}`} aria-label="Hero banner">
      {/* Overlay layers */}
      <div className="hero-overlay" />
      <div className="hero-grid-pattern" aria-hidden="true" />

      {/* Floating particles */}
      <div className="hero-particles" aria-hidden="true">
        {[...Array(12)].map((_, i) => (
          <span key={i} className={`particle p-${i + 1}`} />
        ))}
      </div>

      <div className={`hero-content container ${animating ? 'fade-out' : 'fade-in'}`}>
        <div className="hero-text-block">
          <span className="badge badge-sky hero-badge">{slide.badge}</span>
          <p className="hero-eyebrow">{slide.eyebrow}</p>
          <h1 className="hero-headline">
            {slide.headline.split('\n').map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </h1>
          <p className="hero-sub">{slide.sub}</p>
          <div className="hero-ctas">
            <Link to={slide.cta.to} className="btn btn-primary btn-lg">
              {slide.cta.label}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <Link to={slide.cta2.to} className="btn btn-outline btn-lg">
              {slide.cta2.label}
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="hero-trust">
            <div className="trust-item">
              <span className="trust-num">500+</span>
              <span>Children Certified</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <span className="trust-num">SKIES</span>
              <span>Accredited</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <span className="trust-num">Ages 0–6</span>
              <span>All Stages</span>
            </div>
          </div>
        </div>

        {/* Certification preview card */}
        <div className="hero-cert-preview" aria-hidden="true">
          <div className="cert-card">
            <div className="cert-header">
              <div className="cert-logo-mini">
                <img src="/stratos-logo.png" alt="" width="28" height="28" className="cert-logo-img" />
                <span className="cert-brand">Stratos Standard</span>
              </div>
              <span className="cert-skies-tag">SKIES</span>
            </div>
            <div className="cert-seal-area">
              <div className="cert-seal">
                <svg viewBox="0 0 100 100" width="90" height="90">
                  <circle cx="50" cy="50" r="46" fill="none" stroke="#d4a017" strokeWidth="2" strokeDasharray="4 2"/>
                  <circle cx="50" cy="50" r="38" fill="#0d2045"/>
                  <text x="50" y="45" textAnchor="middle" fill="#f0c040" fontSize="8" fontFamily="Playfair Display" fontWeight="700">CERTIFIED</text>
                  <text x="50" y="57" textAnchor="middle" fill="#d4a017" fontSize="6" fontFamily="Playfair Display">ACHIEVER</text>
                  <path d="M26 65 L50 72 L74 65" stroke="#d4a017" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
            <div className="cert-body">
              <p className="cert-presents">This certifies that</p>
              <p className="cert-name">Your Child's Name</p>
              <p className="cert-award">has achieved the <strong>Stratos Standard</strong></p>
              <div className="cert-stars">★ ★ ★ ★ ★</div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide controls */}
      <div className="slide-controls">
        <button className="slide-arrow prev" onClick={prev} aria-label="Previous slide">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </button>
        <div className="slide-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`slide-dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        <button className="slide-arrow next" onClick={next} aria-label="Next slide">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </button>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator" aria-hidden="true">
        <span className="scroll-line" />
        <span className="scroll-text">Scroll</span>
      </div>
    </section>
  );
}
